// LifeSkillsPage.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Sparkles,
  Heart,
  Users,
  DollarSign,
  Clock,
  Zap,
  MessageSquare,
  CheckSquare,
  BookOpen,
  Mic,
  Volume2,
  Play,
  Pause,
  Square,
} from "lucide-react";

/* ============================
  Helper: speak utility
  ============================ */
const speakText = (text, selectedVoice) => {
  if (typeof window === "undefined") return;
  if ("speechSynthesis" in window) {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.pitch = 1.05;
    if (selectedVoice) utterance.voice = selectedVoice;
    window.speechSynthesis.speak(utterance);
  } else {
    console.warn("Speech Synthesis not supported.");
  }
};

/* ============================
  Data: Life Skills Topics
  (same as your data, kept concise)
  ============================ */
const LIFE_SKILLS_TOPICS = {
  Personal: {
    icon: <Heart className="w-8 h-8" />,
    colorKey: "pink",
    topics: [
      {
        key: "hygiene",
        title: "Daily Hygiene & Health",
        icon: <Heart />,
        colorKey: "pink",
        steps: [
          "Brush your teeth for two minutes.",
          "Wash your face every morning and night.",
          "Take a shower or bath daily.",
          "Change clothes every day.",
          "Get at least 8 hours of sleep.",
        ],
      },
      {
        key: "time",
        title: "Time Management Basics",
        icon: <Clock />,
        colorKey: "orange",
        steps: [
          "Write down three most important tasks for the day.",
          "Estimate how long each task will take.",
          "Use a timer to stay focused on one task.",
          "Schedule short breaks.",
          "Review your progress at the end of the day.",
        ],
      },
      {
        key: "decisions",
        title: "Simple Decision Making",
        icon: <Zap />,
        colorKey: "yellow",
        steps: [
          "Identify the problem or decision needed.",
          "List 2-3 possible choices.",
          "Think about the outcome of each choice.",
          "Pick the best choice and try it!",
          "Learn from the result, even if it wasn't perfect.",
        ],
      },
    ],
  },

  Social: {
    icon: <Users className="w-8 h-8" />,
    colorKey: "blue",
    topics: [
      {
        key: "comm",
        title: "Effective Communication",
        icon: <MessageSquare />,
        colorKey: "blue",
        steps: [
          "Look at the person speaking.",
          "Wait for your turn to talk (don't interrupt).",
          "Speak clearly and at a normal volume.",
          "Ask questions if you don't understand.",
          "Use kind words when expressing disagreement.",
        ],
      },
      {
        key: "team",
        title: "Teamwork and Cooperation",
        icon: <BookOpen />,
        colorKey: "teal",
        steps: [
          "Listen to everyone's ideas.",
          "Offer to help a teammate.",
          "Share materials and resources.",
          "Stay positive and encourage others.",
          "Complete your assigned part of the task.",
        ],
      },
    ],
  },

  Financial: {
    icon: <DollarSign className="w-8 h-8" />,
    colorKey: "green",
    topics: [
      {
        key: "save",
        title: "Saving Money 101",
        icon: <DollarSign />,
        colorKey: "green",
        steps: [
          "Decide what you are saving for (goal).",
          "Put aside a small amount from your allowance each week.",
          "Track your savings on a chart or jar.",
          "Avoid impulse purchases (buying things you didn't plan for).",
          "Watch your money grow!",
        ],
      },
      {
        key: "wants",
        title: "Needs vs. Wants",
        icon: <BookOpen />,
        colorKey: "lime",
        steps: [
          "Identify an item you want to buy.",
          "Determine if it's necessary for survival (Need) or simply desired (Want).",
          "Prioritize Needs over Wants.",
          "If it's a Want, ask yourself if you can wait 24 hours before buying it.",
        ],
      },
    ],
  },
};

/* ============================
  Safe Tailwind class mapping
  (Tailwind needs static class names; map common colors)
  ============================ */
const COLOR_MAP = {
  pink: {
    bgLight: "bg-pink-50",
    text: "text-pink-600",
    border: "border-pink-300",
    ring: "ring-pink-200/60",
  },
  blue: {
    bgLight: "bg-blue-50",
    text: "text-blue-600",
    border: "border-blue-300",
    ring: "ring-blue-200/60",
  },
  teal: {
    bgLight: "bg-teal-50",
    text: "text-teal-600",
    border: "border-teal-300",
    ring: "ring-teal-200/60",
  },
  green: {
    bgLight: "bg-green-50",
    text: "text-green-600",
    border: "border-green-300",
    ring: "ring-green-200/60",
  },
  orange: {
    bgLight: "bg-orange-50",
    text: "text-orange-600",
    border: "border-orange-300",
    ring: "ring-orange-200/60",
  },
  yellow: {
    bgLight: "bg-yellow-50",
    text: "text-yellow-600",
    border: "border-yellow-300",
    ring: "ring-yellow-200/60",
  },
  lime: {
    bgLight: "bg-lime-50",
    text: "text-lime-600",
    border: "border-lime-300",
    ring: "ring-lime-200/60",
  },
  default: {
    bgLight: "bg-gray-50",
    text: "text-gray-700",
    border: "border-gray-200",
    ring: "ring-gray-200/40",
  },
};

/* ============================
  DomainCard (compact)
  - topics inside card
  - topic click also activates parent domain
  ============================ */
const DomainCard = ({
  title,
  icon,
  colorKey,
  topics,
  selectedDomain,
  selectedTopic,
  onDomainSelect,
  onTopicSelect,
}) => {
  const isActive = selectedDomain === title;
  const color = COLOR_MAP[colorKey] || COLOR_MAP.default;

 

  return (
    <div
      className={`rounded-xl border transition-all duration-200 overflow-hidden ${
        isActive ? `${color.ring} shadow-lg` : "bg-white border-gray-200"
      }`}
    >
      {/* Header */}
      <button
        onClick={() => onDomainSelect(title)}
        className={`w-full text-left flex items-center gap-3 p-4 ${
          isActive ? "bg-white/5" : "bg-white"
        }`}
      >
        <div
          className={`w-12 h-12 rounded-full flex items-center justify-center shadow-sm ${
            isActive ? "bg-white" : "bg-gray-100"
          }`}
        >
          {icon}
        </div>

        <div>
          <div className={`text-base font-bold ${isActive ? "text-gray-900" : "text-gray-800"}`}>
            {title}
          </div>
          <div className="text-xs text-gray-500">{topics.length} Skills</div>
        </div>
      </button>

      {/* Topics */}
      <div className="p-3 space-y-2">
        {topics.map((topic) => {
          const isTopicActive = selectedTopic?.key === topic.key;
          const topicColor = COLOR_MAP[topic.colorKey] || COLOR_MAP.default;

          return (
            <button
              key={topic.key}
              onClick={() => {
                // Ensure parent domain is activated too
                onDomainSelect(title);
                onTopicSelect(topic);
              }}
              className={`w-full flex items-center gap-3 p-2 rounded-md text-sm transition-all duration-150 border ${
                isTopicActive
                  ? `${topicColor.bgLight} ${topicColor.border} font-semibold ${topicColor.text} shadow-sm`
                  : "bg-white border-gray-100 hover:bg-gray-50"
              }`}
            >
              {React.cloneElement(topic.icon, {
                className: `w-5 h-5 ${isTopicActive ? topicColor.text : "text-gray-400"}`,
              })}
              <span className={isTopicActive ? topicColor.text : "text-gray-700"}>{topic.title}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ============================
  Activity Display (right column)
  ============================ */
const LifeSkillsActivityDisplay = ({ topic, selectedVoice }) => {
  const speak = (text) => speakText(text, selectedVoice);
  const [isSpeaking, setIsSpeaking] = useState(false);
const [isPaused, setIsPaused] = useState(false);

const utteranceRef = useRef(null);

const handlePlay = (text) => {
  // 1. If currently paused globally -> resume
  if (window.speechSynthesis.paused) {
    window.speechSynthesis.resume();
    setIsPaused(false);
    setIsSpeaking(true); 
    return;
  }

  // 2. If already speaking globally (and not paused) -> do nothing
  if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
    return;
  }
  
  // 3. Otherwise, perform a fresh start

  // CRITICAL FIX: Ensure a fresh start by canceling anything that might be queued or stuck.
  window.speechSynthesis.cancel(); 

  const utterance = new SpeechSynthesisUtterance(text);
  
  // Set properties (optional, but good practice)
  // ... (rate, pitch, voice settings) ...

  utteranceRef.current = utterance;

  utterance.onend = () => {
    // Reset all states when speech naturally concludes or is canceled
    setIsSpeaking(false);
    setIsPaused(false);
  };

  window.speechSynthesis.speak(utterance);
  setIsSpeaking(true);
  setIsPaused(false); 
};

const handlePause = () => {
  // Check if something is actually speaking and not already paused
  if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
    window.speechSynthesis.pause();
    setIsPaused(true);
    // isSpeaking remains true because the speech instance still exists, just suspended
  }
};

const handleStop = () => {
  // Check if anything is active (speaking or paused) before cancelling
  if (window.speechSynthesis.speaking || window.speechSynthesis.paused) {
    // 1. Cancel/Stop the current speech entirely
    window.speechSynthesis.cancel();
    
    // 2. Reset local state
    setIsSpeaking(false);
    setIsPaused(false);
  }
};


  if (!topic) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center bg-gray-50 rounded-2xl shadow-inner text-gray-700 border-2 border-dashed border-gray-200">
        <Sparkles className="w-16 h-16 text-pink-500 mb-5 animate-pulse" />
        <h2 className="text-2xl font-extrabold mb-2">Choose a Life Skill</h2>
        <p className="text-lg">Select a topic from the left to see steps and practice tips.</p>
      </div>
    );
  }

  const topicColor = COLOR_MAP[topic.colorKey] || COLOR_MAP.default;

  return (
    <div className="h-full bg-white p-6 rounded-2xl shadow-2xl border">
      {/* Header */}
      <div className="flex items-start justify-between border-b pb-4 mb-6 border-gray-100">
        <div className="flex items-center gap-3">
          {React.cloneElement(topic.icon, { className: `w-7 h-7 ${topicColor.text}` })}
          <h3 className="text-2xl font-extrabold text-gray-800">{topic.title}</h3>
        </div>
<div className="flex items-center gap-2">
  {/* Play Button */}
  <button
   onClick={() =>handlePlay(
            `Here are the steps for ${topic.title}: ${topic.steps.join(". ")}`
        )}
    // Disable if currently speaking AND not paused (i.e., actively playing)
    disabled={isSpeaking && !isPaused} 
    className={`p-3 rounded-full shadow-md text-white 
      ${(isSpeaking && !isPaused) ? "bg-gray-400" : "bg-green-500 hover:bg-green-600"} 
      transition`}
    title={isPaused ? "Resume" : "Play"}
  >
    <Play className="w-5 h-5" />
  </button>

  {/* Pause Button */}
  <button
    onClick={handlePause}
    // Disable if not speaking OR if already paused
    disabled={!isSpeaking || isPaused} 
    className={`p-3 rounded-full shadow-md text-white 
      ${(!isSpeaking || isPaused) ? "bg-gray-400" : "bg-yellow-500 hover:bg-yellow-600"} 
      transition`}
    title="Pause"
  >
    <Pause className="w-5 h-5" />
  </button>

  {/* Stop/Cancel Button (Highly Recommended) */}
 <button
  onClick={handleStop}
  disabled={!isSpeaking && !isPaused}
  className={`p-3 rounded-full shadow-md text-white 
    ${(!isSpeaking && !isPaused) ? "bg-gray-400" : "bg-red-500 hover:bg-red-600"} 
    transition`}
  title="Reset" // Changed from "Stop" to "Reset"
>
  <Square className="w-5 h-5" /> {/* The visual cue for "Stop/Reset" */}
</button>
  
  <Volume2 className="w-5 h-5 text-gray-800" />
</div>

      </div>

      {/* Steps */}
      <div className={`${topicColor.bgLight} p-4 rounded-lg border ${topicColor.border} space-y-3`}>
        <div className={`flex items-center gap-2 font-semibold ${topicColor.text}`}>
          <CheckSquare className="w-5 h-5" />
          <span>Daily Action Checklist</span>
        </div>

        {topic.steps.map((s, i) => (
          <div key={i} className="flex items-start gap-3 bg-white p-3 rounded-md shadow-sm">
            <div className={`flex items-center justify-center w-8 h-8 rounded-md ${topicColor.text} font-bold`}>
              {i + 1}
            </div>
            <p className="text-gray-700">{s}</p>
          </div>
        ))}
      </div>

      {/* Reflection */}
      <div className="mt-6">
        <h4 className="text-lg font-bold mb-2 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-pink-500" />
          My Goal
        </h4>
        <textarea
          className="w-full h-28 p-3 border border-gray-200 rounded-md focus:border-pink-400 focus:ring-pink-200"
          placeholder={`Write one small goal for ${topic.title} this week...`}
        />
      </div>
    </div>
  );
};

/* ============================
  Main Page
  ============================ */
export default function LifeSkillsPage() {
  const domains = Object.keys(LIFE_SKILLS_TOPICS);
  const [selectedDomain, setSelectedDomain] = useState(domains[0]);
  const [selectedTopic, setSelectedTopic] = useState(LIFE_SKILLS_TOPICS[domains[0]].topics[0]);

  // Speech voices
  const [voices, setVoices] = useState([]);
  const [preferredVoice, setPreferredVoice] = useState(null);
  const voiceListRef = useRef([]);



  useEffect(() => {
    if ("speechSynthesis" in window) {
      const load = () => {
        const vs = window.speechSynthesis.getVoices();
        if (vs.length && voiceListRef.current.length === 0) {
          voiceListRef.current = vs;
          setVoices(vs);
          // pick an english-ish voice if available
          const pref = vs.find((v) => v.lang?.startsWith("en")) || vs[0];
          setPreferredVoice(pref || null);
        }
      };
      load();
      if (window.speechSynthesis.onvoiceschanged !== undefined) {
        window.speechSynthesis.onvoiceschanged = load;
      }
    }
  }, []);

  const handleDomainSelect = (domain) => {
    setSelectedDomain(domain);
    setSelectedTopic(LIFE_SKILLS_TOPICS[domain].topics[0]);
  };

  // Called when clicking a topic — also activates the domain
  const handleTopicSelect = (topic) => {
    // Find the parent domain of this topic
    const parentDomain = Object.keys(LIFE_SKILLS_TOPICS).find((d) =>
      LIFE_SKILLS_TOPICS[d].topics.some((t) => t.key === topic.key)
    );
    if (parentDomain) setSelectedDomain(parentDomain);
    setSelectedTopic(topic);

    // speak a short confirmation
    speakText(`Selected ${topic.title}`, preferredVoice);
  };

  const handleVoiceChange = (e) => {
    const name = e.target.value;
    const v = voices.find((x) => x.name === name);
    setPreferredVoice(v || null);
    if (v) speakText("Voice changed. Hello!", v);
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8] p-6">
      {/* Header - max-w-7xl mx-auto REMOVED */}
      <div className="mb-6 bg-white p-4 rounded-2xl shadow-md flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Heart className="w-8 h-8 text-pink-500" />
          <div>
            <h1 className="text-2xl font-extrabold">Life Skills Toolkit</h1>
            <p className="text-sm text-gray-500">Practical steps kids can learn & practice daily</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {typeof window !== "undefined" && "speechSynthesis" in window && (
            <>
              <div className="flex items-center gap-2 bg-gray-100 p-2 rounded-md border border-gray-200">
                <Mic className="w-5 h-5 text-pink-600" />
                <select
                  value={preferredVoice ? preferredVoice.name : ""}
                  onChange={handleVoiceChange}
                  className="bg-white text-sm p-1 rounded-md"
                >
                  {voices.length === 0 ? <option>Loading voices...</option> : voices.map((v) => (
                    <option key={v.name} value={v.name}>{v.name}</option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Main grid - max-w-7xl mx-auto REMOVED */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* left: domain cards stacked */}
        <div className="lg:col-span-1 space-y-4">
          {domains.map((domain) => {
            const dd = LIFE_SKILLS_TOPICS[domain];
            return (
              <DomainCard
                key={domain}
                title={domain}
                icon={dd.icon}
                colorKey={dd.colorKey}
                topics={dd.topics}
                selectedDomain={selectedDomain}
                selectedTopic={selectedTopic}
                onDomainSelect={handleDomainSelect}
                onTopicSelect={(topic) => {
                  // ensure topic click also activates domain and topic
                  handleTopicSelect(topic);
                }}
              />
            );
          })}
        </div>

        {/* right: activity display */}
        <div className="lg:col-span-3">
          <LifeSkillsActivityDisplay topic={selectedTopic} selectedVoice={preferredVoice} />
        </div>
      </div>
    </div>
  );
}